import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../state";
import { RiPlayFill, RiStopFill } from "react-icons/ri";
import { isEqual } from "lodash";
import {
  StartReplay,
  StopReplay,
} from "../../../utils/graphql/hooks/replayHooks";
import {
  setReplayStateHilightedDriver,
  setReplayStateParamsTimeSkip,
  setReplayStateParamsSkipPreGreenLive,
  setReplayStateParamsSkipNonRealTimeLive,
} from "../../../state/replayReducer";

export default function ReplayControls() {
  const dispatch = useDispatch();
  const startReplay = StartReplay();
  const stopReplay = StopReplay();

  const replayState = useSelector(
    (state: RootState) => state.replay.replayState,
    isEqual,
  );

  const loadingSessionEventData = useSelector(
    (state: RootState) => state.replay.loadingSessionEventData,
  );

  const processedSessionData = useSelector(
    (state: RootState) => state.replay.processedSessionData,
    isEqual,
  );

  const hilightedDriver = useSelector(
    (state: RootState) => state.replay.replayState.hilightedDriver,
  );

  const activeSessionName = useMemo(() => {
    if (replayState.playing) {
      return (
        replayState.processedSessionData?.sessionName || "No Session Selected"
      );
    }
    return processedSessionData?.sessionName || "No Session Selected";
  }, [replayState, processedSessionData]);

  const handlePlayPause = () => {
    if (replayState.playing) {
      stopReplay();
    } else if (replayState.params.sessionId && processedSessionData) {
      startReplay({
        variables: {
          params: {
            sessionId: replayState.params.sessionId,
            timeSkip: replayState.params.timeSkip,
            skipPreGreenLive: replayState.params.skipPreGreenLive,
            skipNonRealTimeLive: replayState.params.skipNonRealTimeLive,
          },
        },
      });
    }
  };

  const handleDriverChange = (driverNumber: string) => {
    dispatch(
      setReplayStateHilightedDriver(driverNumber === "" ? null : driverNumber),
    );
  };

  console.log("ReplayControls render", replayState);

  if (!replayState.connected) {
    return (
      <div className={styles.container}>
        <div className={styles.notConnected}>Replay not connected</div>
      </div>
    );
  }

  if (
    replayState.loadingReplay ||
    (loadingSessionEventData && !replayState.playing)
  ) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <button className={styles.controlButton} onClick={handlePlayPause}>
        {replayState.playing ? <RiStopFill /> : <RiPlayFill />}
      </button>
      <div className={styles.infoContainer}>
        <span>{activeSessionName}</span>

        <div className={styles.infoRow}>
          <input
            type="number"
            value={replayState.params.timeSkip || 0}
            onChange={(e) =>
              dispatch(setReplayStateParamsTimeSkip(Number(e.target.value)))
            }
            className={styles.timeSkipInput}
            disabled={replayState.playing}
          />
          <select
            className={styles.selector}
            value={hilightedDriver ?? ""}
            onChange={(e) => handleDriverChange(e.target.value)}
            disabled={replayState.playing}
          >
            <option value="">Leader</option>
            {processedSessionData?.driverNamesByNumber &&
              Object.entries(processedSessionData.driverNamesByNumber).map(
                ([number, name]) => (
                  <option key={number} value={number}>
                    {name}
                  </option>
                ),
              )}
          </select>
          <label
            className={`${styles.checkboxOption} ${
              replayState.playing ? styles.disabled : ""
            }`}
          >
            <input
              type="checkbox"
              checked={replayState.params.skipPreGreenLive ?? true}
              onChange={(e) =>
                dispatch(setReplayStateParamsSkipPreGreenLive(e.target.checked))
              }
              disabled={replayState.playing}
            />
            {"Skip Pre-Green Live"}
          </label>
          <label
            className={`${styles.checkboxOption} ${
              replayState.playing ? styles.disabled : ""
            }`}
          >
            <input
              type="checkbox"
              checked={replayState.params.skipNonRealTimeLive ?? true}
              onChange={(e) =>
                dispatch(
                  setReplayStateParamsSkipNonRealTimeLive(e.target.checked),
                )
              }
              disabled={replayState.playing}
            />
            {"Skip Non-Real-Time Live"}
          </label>
        </div>
      </div>
    </div>
  );
}
